import { usePublicMetadata } from '../seo/PublicMetadata.jsx';
import React, { useMemo } from 'react';
import {
  AboutHero,
  AboutHowItWorksCards,
  AboutSection,
  BusinessOwnerCta,
  TenantRichTextRenderer,
} from '../components/about/AboutComponents';
import { usePublicTenant } from '../hooks/usePublicTenant';
import { buildPublicPageMetadata } from '../utils/publicMetadata.js';
import { buildBreadcrumbSchema, buildWebPageSchema } from '../utils/structuredData.js';

function getSettingValue(tenant, key) {
  const list = Array.isArray(tenant?.tenantSettings) ? tenant.tenantSettings : [];
  return list.find((item) => item?.settingKey === key)?.settingValue;
}

function cleanString(value) {
  if (typeof value !== 'string') return '';
  return value.trim();
}

function replaceTokens(template, tokens) {
  return String(template || '').replace(/\{\{\s*(tenantName|cityName|siteName|contactEmail)\s*\}\}/g, (_match, key) => tokens[key] || '');
}

function resolveTenantNames({ tenant, spotlightName, contactEmail }) {
  const contact = tenant?.websiteContact || {};
  const tenantName =
    cleanString(contact.publicDisplayName) ||
    cleanString(getSettingValue(tenant, 'tenant.name')) ||
    cleanString(tenant?.displayName) ||
    cleanString(tenant?.name) ||
    'Local Spotlight';

  const cityName =
    cleanString(contact.address?.city) ||
    cleanString(getSettingValue(tenant, 'tenant.city')) ||
    cleanString(tenant?.city) ||
    cleanString(tenant?.marketName) ||
    'your community';

  const siteName =
    cleanString(tenant?.publicName) ||
    cleanString(contact.publicDisplayName) ||
    cleanString(spotlightName) ||
    `${cityName} Spotlight`;

  return {
    tenantName,
    cityName,
    siteName,
    contactEmail: cleanString(contact.contactEmail) || cleanString(contactEmail),
  };
}

function useAboutSeo({ tenant, siteName, cityName, logoUrl, page }) {
  usePublicMetadata(() => {
    if (!tenant) return;
    const title = page?.seoTitle || `About ${siteName} | Local Deals & Businesses in ${cityName}`;
    const description = page?.seoDescription || `Learn about ${siteName}, a local platform helping residents discover nearby businesses, services, restaurants, and deals while helping small businesses reach more local customers.`;
    const metadata = buildPublicPageMetadata({ tenant, pathname: '/about', canonicalPath: '/about', siteName, cityName, logoUrl, title, description });
    metadata.jsonLd = [buildWebPageSchema({ url: metadata.canonicalUrl, name: title, description }), buildBreadcrumbSchema({ url: metadata.canonicalUrl, items: [{ name: 'Home', url: tenant.canonicalOrigin }, { name: `About ${siteName}`, url: metadata.canonicalUrl }] })];
    return (metadata);
  }, [cityName, logoUrl, page?.seoDescription, page?.seoTitle, siteName, tenant]);
}

function DefaultAboutSections({ tokens }) {
  return (
    <>
      <AboutSection title="Who We Are">
        <p>
          {replaceTokens('{{siteName}} is a local discovery and marketing platform built to help residents find trusted businesses, special offers, and useful services in the {{cityName}} area.', tokens)}
        </p>
        <p>
          We work with local restaurants, home service providers, shops, health and wellness businesses, and other community-focused companies to help them reach nearby customers in an affordable and effective way.
        </p>
      </AboutSection>

      <AboutSection title="Our Mission">
        <p>
          {replaceTokens('Our mission is to help small businesses reach more local customers without the high costs of traditional advertising. By combining local visibility, shared marketing opportunities, and digital promotion, {{siteName}} makes it easier for businesses to grow and easier for residents to find great local options.', tokens)}
        </p>
      </AboutSection>

      <AboutSection title="Why We Exist">
        <p>
          {replaceTokens('Local businesses are the heart of every community, but many small business owners struggle to compete with large brands and expensive online advertising. {{siteName}} was created to give those businesses a better way to stay visible in their own backyard.', tokens)}
        </p>
        <p>
          At the same time, we help local residents find nearby deals, services, restaurants, and businesses they may not have discovered otherwise.
        </p>
      </AboutSection>

      <AboutHowItWorksCards cityName={tokens.cityName} />
    </>
  );
}

function AboutPage() {
  const { tenant, spotlightName, contactEmail, tenantLogoImageUrl } = usePublicTenant();
  const page = tenant?.websitePages?.about || null;
  const tokens = useMemo(
    () => resolveTenantNames({ tenant, spotlightName, contactEmail }),
    [tenant, spotlightName, contactEmail],
  );

  const showDefaultSections = page?.showDefaultSections !== false;
  const customContent = cleanString(page?.customContent);
  const customTitle = cleanString(page?.customTitle) || 'Meet the Local Team';

  useAboutSeo({ tenant, siteName: tokens.siteName, cityName: tokens.cityName, logoUrl: tenantLogoImageUrl, page });

  return (
    <div className="min-h-screen overflow-x-hidden bg-surface text-text-primary">
      <AboutHero
        eyebrow="About Us"
        title={`About ${tokens.siteName}`}
        subtitle={`Connecting local residents with the businesses, services, restaurants, and special offers that make ${tokens.cityName} a great place to live, shop, eat, and support local.`}
        logoUrl={tenantLogoImageUrl}
        logoAlt={`${tokens.siteName} logo`}
      />

      {showDefaultSections ? <DefaultAboutSections tokens={tokens} /> : null}

      {customContent ? (
        <AboutSection title={customTitle}>
          <TenantRichTextRenderer content={customContent} />
        </AboutSection>
      ) : null}

      <BusinessOwnerCta siteName={tokens.siteName} />
    </div>
  );
}

export default AboutPage;
